import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr, short, date, today } from '../lib/fmt';
import { Button, Card, Field, Input, Select, Modal, Note, PageHead, Pill, Tag,
         Tile, Tiles, Table, Th, Td, TableWrap, Empty } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from '../components/skeleton';

interface WageBill { total: number; cash: number; banked: number; headcount: number; interns: number }
interface RunSummary { _id: string; period: string; status: string; headcount: number; net: number; paid: number }
interface Line {
  _id: string; gross: number; deductions: number; tds: number; net: number;
  pay_mode: string; status: string; note?: string | null;
  person: { _id: string; name: string; kind: string; designation?: string };
  paid_from?: { _id: string; name: string; kind: string } | null;
  transaction?: { txn_no: string } | null;
}
interface Run {
  _id: string; period: string; status: string; lines: Line[];
  totals: { gross: number; tds: number; net: number; cash: number; bank: number; paid: number; unpaid: number };
}

export default function Payroll() {
  const { id } = useParams();
  return id ? <RunDetail id={id} /> : <RunList />;
}

/** The run list: head, the wage bill, then the runs on file. */
function RunListSkeleton() {
  return (
    <Loading label="Loading payroll">
      <HeadSkeleton actions={1} />
      <TilesSkeleton />
      <CardSkeleton flush head={false}><TableSkeleton rows={5} cols={5} /></CardSkeleton>
    </Loading>
  );
}

/** One run: head, its totals, then a line for each person. */
function RunDetailSkeleton() {
  return (
    <Loading label="Loading the payroll run">
      <HeadSkeleton dek actions={2} />
      <TilesSkeleton />
      <CardSkeleton flush head={false}><TableSkeleton rows={6} cols={6} /></CardSkeleton>
    </Loading>
  );
}

function RunList() {
  const { can } = useSession();
  const nav = useNavigate();
  const feed = useAsync<{ runs: RunSummary[]; wage_bill: WageBill }>(() => api.get('/api/payroll'), []);
  const [open, setOpen] = useState(false);

  if (feed.loading) return <RunListSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const { runs, wage_bill: w } = feed.data!;

  return (
    <div>
      <PageHead eyebrow="People" title="Payroll"
        dek="Staff paid by bank and interns paid in cash sit in one register. The only difference is which account the money leaves from — which is how cash stipends stay inside the books."
        actions={can('payroll:run') && <Button variant="primary" onClick={() => setOpen(true)}>Start a month</Button>} />

      <Tiles>
        <Tile hero label="Monthly wage bill" value={short(w.total)}
              sub={<span className="text-muted">{w.headcount} people</span>} />
        <Tile label="Paid in cash" value={short(w.cash)}
              sub={<span className="text-muted">{w.interns} intern{w.interns === 1 ? '' : 's'} — the part that usually escapes the books</span>} />
        <Tile label="Paid by bank" value={short(w.banked)} sub={<span className="text-muted">salaried staff</span>} />
        <Tile label="Runs on file" value={String(runs.length)}
              sub={<span className="text-muted">{runs[0] ? `latest ${runs[0].period}` : 'none yet'}</span>} />
      </Tiles>

      <Card flush>
        {!runs.length ? <Empty>No payroll run yet. Start a month to build one from the people on file.</Empty> : (
          <TableWrap>
            <Table cards>
              <thead><tr><Th>Period</Th><Th>Headcount</Th><Th>Status</Th><Th n>Net</Th><Th n>Paid</Th><Th /></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r._id} tabIndex={0} className="cursor-pointer hover:bg-card-2"
                      onClick={() => nav(`/payroll/${r._id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') nav(`/payroll/${r._id}`); }}>
                    <Td label="Period" className="font-mono text-[11.5px]">{r.period}</Td>
                    <Td label="Headcount">{r.headcount} people</Td>
                    <Td label="Status"><Pill kind={r.status === 'paid' ? 'paid' : r.status === 'approved' ? 'approved' : 'draft'}>{r.status}</Pill></Td>
                    <Td label="Net" n>{inr(r.net)}</Td>
                    <Td label="Paid" n className={r.paid < r.net ? 'text-critical' : 'text-good'}>{inr(r.paid)}</Td>
                    <Td label="" className="text-right font-mono text-muted">open →</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {open && <StartModal onClose={() => setOpen(false)} onDone={(rid) => nav(`/payroll/${rid}`)} />}
    </div>
  );
}

function StartModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <Modal title="Start a payroll month" onClose={onClose} confirmLabel="Build the run" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try { const r = await api.post<Run>('/api/payroll', { period }); onDone(r._id); }
        catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <p className="text-[13px] text-ink-2 mb-3.5">
        Everyone active in that month is pulled in with their contracted amount. You can change any
        line before approving.
      </p>
      <Field label="Month"><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} /></Field>
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function RunDetail({ id }: { id: string }) {
  const { can, meta } = useSession();
  const feed = useAsync<Run>(() => api.get(`/api/payroll/${id}`), [id]);
  const [edit, setEdit] = useState<Line | null>(null);
  const [pay, setPay] = useState(false);
  const [err, setErr] = useState('');

  if (feed.loading) return <RunDetailSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const r = feed.data!;

  async function approve() {
    setErr('');
    try { await api.post(`/api/payroll/${id}/approve`, {}); feed.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
  }

  return (
    <div>
      <PageHead eyebrow={<Link to="/payroll" className="text-inherit no-underline">← Payroll</Link>}
        title={`Payroll · ${r.period}`}
        dek={`${r.lines.length} people · net ${inr(r.totals.net)}`}
        actions={<>
          <Pill kind={r.status === 'paid' ? 'paid' : r.status === 'approved' ? 'approved' : 'draft'}>{r.status}</Pill>
          {r.status === 'draft' && can('payroll:approve') &&
            <Button variant="primary" onClick={() => void approve()}>Approve run</Button>}
          {r.status === 'approved' && can('payroll:approve') &&
            <Button variant="primary" onClick={() => setPay(true)}>Pay {inr(r.totals.unpaid)}</Button>}
        </>} />

      {err && <Note title="That did not work" tone="critical">{err}</Note>}

      <Tiles>
        <Tile hero label="Net payable" value={inr(r.totals.net)}
              sub={<span className="text-muted">gross {inr(r.totals.gross)}, TDS {inr(r.totals.tds)}</span>} />
        <Tile label="From cash" value={inr(r.totals.cash)}
              sub={<span className="text-muted">draws down the office float</span>} />
        <Tile label="From bank" value={inr(r.totals.bank)} sub={<span className="text-muted">salaried transfers</span>} />
        <Tile label="Still to pay" value={inr(r.totals.unpaid)}
              sub={<span className="text-muted">{r.totals.unpaid ? 'not yet disbursed' : 'fully disbursed'}</span>} />
      </Tiles>

      <Card flush>
        <TableWrap>
          <Table cards>
            <thead><tr>
              <Th>Person</Th><Th>Type</Th><Th n>Gross</Th><Th n>TDS</Th><Th n>Net</Th>
              <Th>Mode</Th><Th>From</Th><Th>Status</Th><Th />
            </tr></thead>
            <tbody>
              {r.lines.map((l) => (
                <tr key={l._id}>
                  <Td label="Person"><b>{l.person.name}</b>{l.person.designation &&
                    <><br /><span className="text-muted text-[11.5px]">{l.person.designation}</span></>}</Td>
                  <Td label="Type"><Tag>{l.person.kind}</Tag></Td>
                  <Td label="Gross" n>{inr(l.gross)}</Td>
                  <Td label="TDS" n className="text-muted">{l.tds ? inr(l.tds) : '—'}</Td>
                  <Td label="Net" n><b>{inr(l.net)}</b></Td>
                  <Td label="Mode"><Tag tone={l.pay_mode === 'cash' ? 'brand' : undefined}>{l.pay_mode}</Tag></Td>
                  <Td label="From" className="text-ink-2">{l.paid_from?.name ?? '—'}</Td>
                  <Td label="Status"><Pill kind={l.status === 'paid' ? 'paid' : 'draft'}>{l.status}</Pill></Td>
                  <Td label="" className="text-right">
                    {l.status === 'pending' && r.status === 'draft' && can('payroll:run')
                      ? <Button size="sm" variant="ghost" onClick={() => setEdit(l)}>Edit</Button>
                      : l.transaction && <span className="font-mono text-muted text-[11.5px]">{l.transaction.txn_no}</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-rule font-semibold">
                <td colSpan={2} className="px-3 py-2.5">Total</td>
                <td className="px-3 py-2.5 text-right tnum">{inr(r.totals.gross)}</td>
                <td className="px-3 py-2.5 text-right tnum">{inr(r.totals.tds)}</td>
                <td className="px-3 py-2.5 text-right tnum">{inr(r.totals.net)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </Table>
        </TableWrap>
      </Card>

      {edit && <LineModal runId={id} line={edit} accounts={meta?.accounts ?? []}
                          onClose={() => setEdit(null)} onDone={() => { setEdit(null); feed.reload(); }} />}
      {pay && <DisburseModal run={r} onClose={() => setPay(false)}
                             onDone={() => { setPay(false); feed.reload(); }} />}
    </div>
  );
}

function LineModal(
  { runId, line, accounts, onClose, onDone }:
  { runId: string; line: Line; accounts: { id: string; name: string }[];
    onClose: () => void; onDone: () => void },
) {
  const [gross, setGross] = useState((line.gross / 100).toFixed(2));
  const [ded, setDed] = useState((line.deductions / 100).toFixed(2));
  const [tds, setTds] = useState((line.tds / 100).toFixed(2));
  const [mode, setMode] = useState(line.pay_mode);
  const [account, setAccount] = useState(line.paid_from?._id ?? accounts[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  return (
    <Modal title={`Edit ${line.person.name}`} wide onClose={onClose} confirmLabel="Save line" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.patch(`/api/payroll/${runId}/lines/${line._id}`, {
            gross: Math.round(Number(gross) * 100),
            deductions: Math.round(Number(ded) * 100),
            tds: Math.round(Number(tds) * 100),
            pay_mode: mode, paid_from: account,
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Gross (₹)"><Input type="number" step="0.01" value={gross} onChange={(e) => setGross(e.target.value)} /></Field>
        <Field label="Deductions (₹)"><Input type="number" step="0.01" value={ded} onChange={(e) => setDed(e.target.value)} /></Field>
        <Field label="TDS (₹)"><Input type="number" step="0.01" value={tds} onChange={(e) => setTds(e.target.value)} /></Field>
        <Field label="Pay by">
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            {['bank','cash','upi'].map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </Field>
        <Field label="Paid from" span>
          <Select value={account} onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function DisburseModal({ run, onClose, onDone }: { run: Run; onClose: () => void; onDone: () => void }) {
  const [when, setWhen] = useState(today());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pending = run.lines.filter((l) => l.status === 'pending').length;
  return (
    <Modal title={`Disburse ${run.period}`} onClose={onClose}
      confirmLabel={`Pay ${inr(run.totals.unpaid)}`} busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try { await api.post(`/api/payroll/${run._id}/disburse`, { paid_on: when }); onDone(); }
        catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <p className="text-[13px] text-ink-2 mb-3.5">
        {inr(run.totals.unpaid)} across {pending} people. Each line draws from its own account —
        {' '}{inr(run.totals.cash)} comes out of cash.
      </p>
      <Field label="Paid on"><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}
